import { useInfinitePagination, useResources } from '@funtech-int/rival-react';
import { Selection } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createInfiniteQuery } from '@funtech-int/rival';
import { useAuthManager } from '@funtech-int/yango-shared';

import { queryVariables } from '@/shared/constants/queryVariables';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useProfileManager } from '@/shared/hooks/useProfileManager';

import { MovieOverviewSelectionType, registry, SELECTIONS_ORDER } from '../lib/movieOverview';
import { MovieOverviewSelectionsQueryContext } from '../lib/overview';

import { useI18n } from './useI18n';

const OVERVIEW_LIST_UPDATE_DELAY = 60 * 60 * 1000;

export type MovieOverviewSelection = Omit<Selection, 'type' | 'typename'> & {
    type: MovieOverviewSelectionType;
    typename: 'DefaultSelection';
};

interface MovieOverviewSelectionsPageInfo {
    selectionType: MovieOverviewSelectionType;
}

interface MovieOverviewSelectionsQueryResult {
    data: {
        items: MovieOverviewSelection[];
    };
    errors?: unknown;
    requestId: string;
    meta: {
        selectionType: MovieOverviewSelectionType;
    };
}

const getNextSelectionType = (
    current?: MovieOverviewSelectionType
): MovieOverviewSelectionType | undefined => {
    if (!current) return SELECTIONS_ORDER[0];

    const index = SELECTIONS_ORDER.indexOf(current);
    return SELECTIONS_ORDER[index + 1];
};

interface MovieOverviewQueryResult {
    items: MovieOverviewSelection[];
    errors: unknown;
    requestId: string;
}

const movieOverviewSelectionsResourceQuery = createInfiniteQuery<
    MovieOverviewSelectionsQueryContext,
    MovieOverviewSelectionsQueryResult,
    MovieOverviewSelectionsPageInfo
>({
    key(ctx: MovieOverviewSelectionsQueryContext): string {
        return [
            'movieOverviewSelections',
            ctx.contentId,
            ctx.withUserData,
            ctx.withRating,
            ctx.billingFeaturesCount,
        ].join(':');
    },
    async fetch(ctx, prev, pageInfo) {
        const selectionType = pageInfo?.selectionType ?? SELECTIONS_ORDER[0]!;

        const items = [...(prev?.data.items ?? [])];

        let result: MovieOverviewQueryResult = {
            items: [],
            errors: [],
            requestId: '',
        };

        try {
            result = await registry[selectionType](ctx);
            items.push(...result.items);
        } catch (e) {
            /* empty */
        }

        return {
            data: { items },
            errors: result.errors,
            requestId: result.requestId,
            meta: { selectionType },
        };
    },
    nextPageInfo(data) {
        const next = getNextSelectionType(data?.meta.selectionType);
        return next ? { selectionType: next } : undefined;
    },
    toJSON(data: any) {
        return data;
    },
    hydrate(data: any) {
        return data;
    },
});

interface UseMovieOverviewDataParams {
    contentId: string;
}

export function useMovieOverviewQuery({ contentId }: UseMovieOverviewDataParams) {
    const i18n = useI18n();
    const client = useGraphqlClient();
    const { isAuthorized } = useAuthManager();
    const pagination = useInfinitePagination();
    const billingFeaturesCount = useProfileManager().billingFeatureNames.get().length;

    const [result] = useResources(
        movieOverviewSelectionsResourceQuery(
            {
                contentId,
                withUserData: isAuthorized,
                withRating: queryVariables.withRating,
                client,
                i18n,
                billingFeaturesCount,
            },
            { pagination, lifetime: OVERVIEW_LIST_UPDATE_DELAY }
        )
    );

    const currentSelectionType = result?.meta?.selectionType;
    const hasMore = getNextSelectionType(currentSelectionType) !== undefined;

    return {
        selections: result.data.items,
        hasMore,
        pagination,
    };
}
